/**
 * ExplorePage Component
 * Browse and discover movies via trending, popular, top-rated, now playing, and search.
 * Supports search by title, actor, director, genre, decade, keywords, language, and country.
 * State is persisted in URL search params so it survives navigation (back button).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  discoverMovies,
  getCountries,
  getGenres,
  getLanguages,
  getNowPlayingMovies,
  getPopularMovies,
  getTopRatedMovies,
  getTrendingMovies,
  searchMoviesByPerson,
  type CountryOption,
  type DiscoveryMovie,
  type Genre,
  type LanguageOption,
  type PersonSearchResponse,
} from '../api';

type Category = 'trending' | 'popular' | 'top-rated' | 'now-playing';
type ExploreDomain = 'discover' | 'people';
type QueryScope = 'title' | 'keywords';
type PeopleRole = 'actor' | 'director';

const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'trending', label: 'Trending' },
  { id: 'popular', label: 'Popular' },
  { id: 'top-rated', label: 'Top Rated' },
  { id: 'now-playing', label: 'Now Playing' },
];

const DOMAINS: { id: ExploreDomain; label: string }[] = [
  { id: 'discover', label: 'Discover' },
  { id: 'people', label: 'People' },
];

const QUERY_SCOPES: { id: QueryScope; label: string }[] = [
  { id: 'title', label: 'Title' },
  { id: 'keywords', label: 'Keywords' },
];

const PEOPLE_ROLES: { id: PeopleRole; label: string }[] = [
  { id: 'actor', label: 'Actor' },
  { id: 'director', label: 'Director' },
];

const DECADES = [
  { year: 2020, label: '2020s' },
  { year: 2010, label: '2010s' },
  { year: 2000, label: '2000s' },
  { year: 1990, label: '1990s' },
  { year: 1980, label: '1980s' },
  { year: 1970, label: '1970s' },
  { year: 1960, label: '1960s' },
  { year: 1950, label: '1950s' },
  { year: 1940, label: '1940s' },
  { year: 1930, label: '1930s' },
  { year: 1920, label: '1920s' },
];

function isCategory(value: string): value is Category {
  return ['trending', 'popular', 'top-rated', 'now-playing'].includes(value);
}

function isDomain(value: string): value is ExploreDomain {
  return ['discover', 'people'].includes(value);
}

function isQueryScope(value: string): value is QueryScope {
  return ['title', 'keywords'].includes(value);
}

function isPeopleRole(value: string): value is PeopleRole {
  return ['actor', 'director'].includes(value);
}

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL params
  const urlQuery = searchParams.get('q') || '';
  const urlDomain = searchParams.get('domain') || 'discover';
  const urlScope = searchParams.get('scope') || 'keywords';
  const urlRole = searchParams.get('role') || 'actor';
  const urlCat = searchParams.get('cat') || 'trending';
  const urlPage = searchParams.get('page') || '1';
  const urlGenre = searchParams.get('genre') || '';
  const urlDecade = searchParams.get('decade') || '';
  const urlLanguage = searchParams.get('language') || '';
  const urlCountry = searchParams.get('country') || '';

  const [category, setCategory] = useState<Category>(isCategory(urlCat) ? urlCat : 'trending');
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(urlQuery);
  const [domain, setDomain] = useState<ExploreDomain>(isDomain(urlDomain) ? urlDomain : 'discover');
  const [queryScope, setQueryScope] = useState<QueryScope>(
    isQueryScope(urlScope) ? urlScope : 'title'
  );
  const [peopleRole, setPeopleRole] = useState<PeopleRole>(
    isPeopleRole(urlRole) ? urlRole : 'actor'
  );
  const [movies, setMovies] = useState<DiscoveryMovie[]>([]);
  const [personName, setPersonName] = useState<string | null>(null);
  const [page, setPage] = useState(Math.max(1, parseInt(urlPage, 10) || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reference data for discover filters
  const [genres, setGenres] = useState<Genre[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<number | null>(
    urlGenre ? Number(urlGenre) : null
  );
  const [selectedDecade, setSelectedDecade] = useState<number | null>(
    urlDecade ? Number(urlDecade) : null
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(urlLanguage || null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(urlCountry || null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Load reference data once
  const loadedRef = useRef<{ genres: boolean; languages: boolean; countries: boolean }>({
    genres: false,
    languages: false,
    countries: false,
  });

  useEffect(() => {
    if (!loadedRef.current.genres) {
      loadedRef.current.genres = true;
      getGenres().then(setGenres).catch(console.error);
    }
    if (!loadedRef.current.languages) {
      loadedRef.current.languages = true;
      getLanguages().then(setLanguages).catch(console.error);
    }
    if (!loadedRef.current.countries) {
      loadedRef.current.countries = true;
      getCountries().then(setCountries).catch(console.error);
    }
  }, []);

  // Helpers for display labels
  const getGenreName = useCallback(
    (id: number) => genres.find((g) => g.id === id)?.name || `Genre ${id}`,
    [genres]
  );
  const getLanguageName = useCallback(
    (code: string) => languages.find((l) => l.code === code)?.name || code,
    [languages]
  );
  const getCountryName = useCallback(
    (code: string) => countries.find((c) => c.code === code)?.name || code,
    [countries]
  );

  const hasDiscoverFilters =
    selectedGenre !== null ||
    selectedDecade !== null ||
    selectedLanguage !== null ||
    selectedCountry !== null;
  const hasTextSearch = debouncedQuery.trim().length >= 2;
  const isDiscoverDomain = domain === 'discover';

  // Sync state to URL params (replaces history entry so back button works)
  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedQuery) params.q = debouncedQuery;
    if (domain !== 'discover') params.domain = domain;
    if (queryScope !== 'keywords') params.scope = queryScope;
    if (peopleRole !== 'actor') params.role = peopleRole;
    if (
      !debouncedQuery &&
      !hasDiscoverFilters &&
      domain === 'discover' &&
      category !== 'trending'
    ) {
      params.cat = category;
    }
    if (page > 1) params.page = String(page);
    if (selectedGenre) params.genre = String(selectedGenre);
    if (selectedDecade) params.decade = String(selectedDecade);
    if (selectedLanguage) params.language = selectedLanguage;
    if (selectedCountry) params.country = selectedCountry;

    setSearchParams(params, { replace: true });
  }, [
    debouncedQuery,
    domain,
    queryScope,
    peopleRole,
    category,
    page,
    selectedGenre,
    selectedDecade,
    selectedLanguage,
    selectedCountry,
    hasDiscoverFilters,
    setSearchParams,
  ]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when category, search, or search mode changes
  useEffect(() => {
    setPage(1);
    setPersonName(null);
  }, [
    category,
    debouncedQuery,
    domain,
    queryScope,
    peopleRole,
    selectedGenre,
    selectedDecade,
    selectedLanguage,
    selectedCountry,
  ]);

  const handleClearAll = () => {
    setSearchQuery('');
    setDebouncedQuery('');
    setSelectedGenre(null);
    setSelectedDecade(null);
    setSelectedLanguage(null);
    setSelectedCountry(null);
  };

  // Fetch movies based on discover filters/category or people search
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchMovies() {
      const hasText = debouncedQuery.trim().length >= 2;

      if (domain === 'people') {
        if (!hasText) {
          setMovies([]);
          setTotalPages(1);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const response: PersonSearchResponse = await searchMoviesByPerson(
            debouncedQuery,
            peopleRole,
            signal
          );
          setMovies(response.results);
          setPersonName(response.person?.name || null);
          setTotalPages(response.totalPages);
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setError(err instanceof Error ? err.message : 'Failed to load movies');
        } finally {
          if (!signal.aborted) setLoading(false);
        }
        return;
      }

      // Discover mode with additive filters and optional text scope
      if (hasText || hasDiscoverFilters) {
        setLoading(true);
        setError(null);
        setPersonName(null);
        try {
          const response = await discoverMovies(
            {
              page,
              genre: selectedGenre ?? undefined,
              decade: selectedDecade ?? undefined,
              language: selectedLanguage ?? undefined,
              country: selectedCountry ?? undefined,
              queryText: hasText ? debouncedQuery : undefined,
              queryScope: hasText ? queryScope : undefined,
            },
            signal
          );
          setMovies(response.results);
          setTotalPages(response.totalPages);
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setError(err instanceof Error ? err.message : 'Failed to load movies');
        } finally {
          if (!signal.aborted) setLoading(false);
        }
        return;
      }

      // Discover category mode (no active text/filter)
      setLoading(true);
      setError(null);
      setPersonName(null);
      try {
        let response;
        switch (category) {
          case 'trending':
            response = await getTrendingMovies(page, 'week', signal);
            break;
          case 'popular':
            response = await getPopularMovies(page, signal);
            break;
          case 'top-rated':
            response = await getTopRatedMovies(page, signal);
            break;
          case 'now-playing':
            response = await getNowPlayingMovies(page, signal);
            break;
        }
        setMovies(response.results);
        setTotalPages(response.totalPages);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load movies');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    }

    fetchMovies();

    return () => controller.abort();
  }, [
    domain,
    queryScope,
    peopleRole,
    category,
    debouncedQuery,
    page,
    selectedGenre,
    selectedDecade,
    selectedLanguage,
    selectedCountry,
    hasDiscoverFilters,
  ]);

  const showCategoryTabs = isDiscoverDomain && !hasTextSearch && !hasDiscoverFilters;

  const getResultsHeader = (): string | null => {
    if (domain === 'people') {
      if (!hasTextSearch) return null;
      if (personName) {
        return peopleRole === 'actor'
          ? `Movies starring ${personName}`
          : `Movies directed by ${personName}`;
      }
      return peopleRole === 'actor'
        ? `Actor results for \u201c${debouncedQuery}\u201d`
        : `Director results for \u201c${debouncedQuery}\u201d`;
    }

    if (hasTextSearch && hasDiscoverFilters) {
      const scopeLabel = queryScope === 'title' ? 'title' : 'keyword';
      return `Showing ${scopeLabel} matches for \u201c${debouncedQuery}\u201d with filters`;
    }

    if (hasTextSearch) {
      return queryScope === 'title'
        ? `Title results for \u201c${debouncedQuery}\u201d`
        : `Keyword results for \u201c${debouncedQuery}\u201d`;
    }

    if (hasDiscoverFilters) {
      return 'Showing movies matching filters';
    }

    return null;
  };

  const activeChips: { id: string; label: string; onRemove: () => void }[] = [];
  if (selectedGenre) {
    activeChips.push({
      id: 'genre',
      label: `Genre: ${getGenreName(selectedGenre)}`,
      onRemove: () => setSelectedGenre(null),
    });
  }
  if (selectedDecade) {
    activeChips.push({
      id: 'decade',
      label: `Decade: ${selectedDecade}s`,
      onRemove: () => setSelectedDecade(null),
    });
  }
  if (selectedLanguage) {
    activeChips.push({
      id: 'language',
      label: `Language: ${getLanguageName(selectedLanguage)}`,
      onRemove: () => setSelectedLanguage(null),
    });
  }
  if (selectedCountry) {
    activeChips.push({
      id: 'country',
      label: `Country: ${getCountryName(selectedCountry)}`,
      onRemove: () => setSelectedCountry(null),
    });
  }
  if (hasTextSearch) {
    activeChips.push({
      id: 'text',
      label:
        domain === 'people'
          ? `Text: ${debouncedQuery} (${peopleRole})`
          : `Text: ${debouncedQuery} (${queryScope})`,
      onRemove: () => {
        setSearchQuery('');
        setDebouncedQuery('');
      },
    });
  }

  const resultsHeader = getResultsHeader();
  const discoverSummaryParts: string[] = [];
  if (selectedGenre) discoverSummaryParts.push(`Genre: ${getGenreName(selectedGenre)}`);
  if (selectedDecade) discoverSummaryParts.push(`Decade: ${selectedDecade}s`);
  if (selectedLanguage) discoverSummaryParts.push(`Language: ${getLanguageName(selectedLanguage)}`);
  if (selectedCountry) discoverSummaryParts.push(`Country: ${getCountryName(selectedCountry)}`);
  const discoverFiltersSummary =
    discoverSummaryParts.length > 0 ? discoverSummaryParts.join(' · ') : 'No filters selected';
  const searchPlaceholder =
    domain === 'people'
      ? peopleRole === 'actor'
        ? 'Search actor name...'
        : 'Search director name...'
      : queryScope === 'title'
        ? 'Search by movie title...'
        : 'Search by keywords (e.g. haunted, revenge)...';

  return (
    <div className="explore-page">
      <div className="explore-search-modes">
        {DOMAINS.map((item) => (
          <button
            key={item.id}
            className={`explore-search-mode ${domain === item.id ? 'active' : ''}`}
            onClick={() => setDomain(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="explore-search">
        <div className="explore-search-input-wrapper">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="explore-search-icon"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="explore-search-input"
          />
          {searchQuery && (
            <button
              className="explore-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="explore-active-filters">
          <div className="explore-active-filter-chips">
            {activeChips.map((chip) => (
              <button key={chip.id} className="explore-active-chip" onClick={chip.onRemove}>
                {chip.label}
                <span aria-hidden>×</span>
              </button>
            ))}
          </div>
          <button className="explore-clear-all" onClick={handleClearAll}>
            Clear all
          </button>
        </div>
      )}

      {domain === 'discover' && (
        <div className="explore-filters-panel">
          <button
            className="explore-filters-summary"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            aria-expanded={filtersExpanded}
          >
            <div className="explore-filters-summary-text">
              <span className="explore-filters-summary-title">Filters</span>
              <span>{discoverFiltersSummary}</span>
              <span>Search In: {queryScope === 'title' ? 'Title' : 'Keywords'}</span>
            </div>
            <span className="explore-filters-summary-chevron">{filtersExpanded ? '▲' : '▼'}</span>
          </button>

          {filtersExpanded && (
            <div className="explore-filters-expanded">
              <div className="explore-filter-group">
                <span className="explore-filter-label">Search In</span>
                <div className="explore-search-modes">
                  {QUERY_SCOPES.map((scope) => (
                    <button
                      key={scope.id}
                      className={`explore-search-mode ${queryScope === scope.id ? 'active' : ''}`}
                      onClick={() => setQueryScope(scope.id)}
                    >
                      {scope.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="explore-filter-group">
                <span className="explore-filter-label">Genre</span>
                <div className="explore-picker-scroll">
                  {genres.map((genre) => (
                    <button
                      key={genre.id}
                      className={`explore-picker-chip ${selectedGenre === genre.id ? 'active' : ''}`}
                      onClick={() => setSelectedGenre(selectedGenre === genre.id ? null : genre.id)}
                    >
                      {genre.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="explore-filter-group">
                <span className="explore-filter-label">Decade</span>
                <div className="explore-picker-scroll">
                  {DECADES.map((d) => (
                    <button
                      key={d.year}
                      className={`explore-picker-chip ${selectedDecade === d.year ? 'active' : ''}`}
                      onClick={() => setSelectedDecade(selectedDecade === d.year ? null : d.year)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="explore-filter-selects">
                <label className="explore-filter-select">
                  <span className="explore-filter-label">Language</span>
                  <select
                    value={selectedLanguage ?? ''}
                    onChange={(e) => setSelectedLanguage(e.target.value || null)}
                  >
                    <option value="">Any language</option>
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="explore-filter-select">
                  <span className="explore-filter-label">Country</span>
                  <select
                    value={selectedCountry ?? ''}
                    onChange={(e) => setSelectedCountry(e.target.value || null)}
                  >
                    <option value="">Any country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {domain === 'people' && (
        <div className="explore-search-modes">
          {PEOPLE_ROLES.map((role) => (
            <button
              key={role.id}
              className={`explore-search-mode ${peopleRole === role.id ? 'active' : ''}`}
              onClick={() => setPeopleRole(role.id)}
            >
              {role.label}
            </button>
          ))}
        </div>
      )}

      {/* Category Tabs */}
      {showCategoryTabs && (
        <div className="explore-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`explore-tab ${category === cat.id ? 'active' : ''}`}
              onClick={() => setCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Results Header */}
      {resultsHeader && (
        <div className="explore-results-header">
          <h2>{resultsHeader}</h2>
        </div>
      )}

      {/* Error State */}
      {error && <div className="explore-error">{error}</div>}

      {/* Loading State */}
      {loading && (
        <div className="explore-loading">
          <div className="spinner" />
          <span>Loading movies...</span>
        </div>
      )}

      {/* Movie Grid */}
      {!loading && !error && (
        <>
          {movies.length === 0 ? (
            <div className="explore-empty">
              {domain === 'people' && !hasTextSearch ? (
                <p>Type at least 2 letters to search people.</p>
              ) : hasTextSearch || hasDiscoverFilters ? (
                <p>No movies found</p>
              ) : (
                <p>Pick filters or browse categories to discover movies</p>
              )}
            </div>
          ) : (
            <div className="explore-grid">
              {movies.map((movie) => (
                <Link
                  key={movie.tmdb_id}
                  to={`/movies/${movie.tmdb_id}`}
                  className="explore-movie-card"
                >
                  <div className="explore-movie-poster">
                    {movie.poster_url ? (
                      <img src={movie.poster_url} alt={movie.title} loading="lazy" />
                    ) : (
                      <div className="explore-movie-poster-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    {/* Status badges - top left */}
                    {(movie.is_watched || movie.is_want_to_watch) && (
                      <div className="explore-movie-badges">
                        {movie.is_watched && (
                          <span className="explore-movie-badge explore-movie-badge-watched">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          </span>
                        )}
                        {movie.is_want_to_watch && !movie.is_watched && (
                          <span className="explore-movie-badge explore-movie-badge-want-to-watch">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                          </span>
                        )}
                      </div>
                    )}
                    {/* Rating badge - top right */}
                    {movie.imdb_rating ? (
                      <div className="explore-movie-rating explore-movie-rating-imdb">
                        <span className="explore-movie-rating-label">IMDb</span>
                        <span>{movie.imdb_rating}</span>
                      </div>
                    ) : (
                      movie.vote_average > 0 && (
                        <div className="explore-movie-rating">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          <span>{movie.vote_average.toFixed(1)}</span>
                        </div>
                      )
                    )}
                  </div>
                  <div className="explore-movie-info">
                    <h3 className="explore-movie-title">{movie.original_title || movie.title}</h3>
                    {movie.year && <span className="explore-movie-year">{movie.year}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="explore-pagination">
              <button
                className="secondary"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="explore-pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="secondary"
                onClick={() => {
                  setPage((p) => Math.min(totalPages, p + 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
